import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Image, Platform, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, NavigationHeader, Spacer } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconPot from 'design-system/icons/IconPot';
import RadioButton from 'design-system/RadioButton';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { SaveFunnelEvents } from 'features/funnel-events/types';
import { amplitudeSourceForPots } from 'features/invest/utils/analytics';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import LinkHelpers from 'utils/links';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { OnboardingFlow } from 'utils/types/navigation';
import { savePotsOnboardingState } from '../actions';
import ListInfoItem from '../components/ListInfoItem';
import useCreatePot from '../hooks/useCreatePot';
import useNavigateToNextScreenForPotsOnboarding from '../hooks/useNavigateToNextScreenForPotsOnboarding';
import { selectActivePotInterest, selectIsPotsAccountActive, selectPotProductFromProductId, } from '../reducers/selectors';
import { getPotEventName } from '../utils';
const FSCSImg = require('../img/FSCS.png');
const ReviewAndConfirmScreen = ({ navigation, route }) => {
    const dispatch = useAppDispatch();
    const saveEnabledRef = useRef(true);
    const scrollY = useSharedValue(0);
    const productId = route.params?.productId;
    const createPotParams = route.params?.createPotParams;
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const potsOnboardingState = useAppSelector((state) => state.pots.onboarding);
    const isPotsAccountActive = useAppSelector(selectIsPotsAccountActive);
    const activePotInterest = useAppSelector((store) => selectActivePotInterest(store, productId));
    const interestProduct = useAppSelector((store) => selectPotProductFromProductId(store, productId));
    const [value, setValue] = useState(false);
    const { createNewPot, creatingPotRequesting } = useCreatePot();
    const navigateToNextScreen = useNavigateToNextScreenForPotsOnboarding();
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, colors.text.white]);
    useEffect(() => {
        /**
         * Flow for Create Pots
         */
        if (createPotParams && !mountedOnAnotherScreen) {
            const { type } = createPotParams;
            Amplitude.logEvent('Pot Consent Screen', {
                source: amplitudeSourceForPots,
                product: getPotEventName(type),
            });
        }
    }, [createPotParams, mountedOnAnotherScreen]);
    const styles = useStyles(styleSet);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    });
    const handleContinue = useCallback(async () => {
        if (createPotParams && saveEnabledRef.current && productId) {
            const { name, selectedEmoji, type, goalAmount, date } = createPotParams;
            const createPot = {
                name,
                type,
                productId,
                emoji: selectedEmoji,
                ...(goalAmount
                    ? {
                        goal: {
                            date,
                            amount: Number(goalAmount),
                        },
                    }
                    : {}),
            };
            const isCashIsa = !!interestProduct?.isProductCashISA;
            if (isCashIsa) {
                if (potsOnboardingState && !isPotsAccountActive) {
                    dispatch(postFunnelEvents([SaveFunnelEvents.ONB_SAVE_NEXT_ISA_DECLARATION]));
                }
                navigation.navigate('InvestSecclISADeclarationScreen', { createPotParams: createPot });
                return;
            }
            if (potsOnboardingState && !isPotsAccountActive) {
                dispatch(savePotsOnboardingState({ createPotsParams: createPot }));
                navigateToNextScreen();
                return;
            }
            saveEnabledRef.current = false;
            await createNewPot(createPot);
            saveEnabledRef.current = true;
        }
    }, [
        createNewPot,
        createPotParams,
        interestProduct?.isProductCashISA,
        isPotsAccountActive,
        navigateToNextScreen,
        potsOnboardingState,
        productId,
    ]);
    const handleToggle = useCallback(() => {
        setValue((oldValue) => !oldValue);
    }, []);
    const handleFAQs = useCallback(() => {
        if (interestProduct?.id) {
            navigation.navigateDeprecated('InterestPotsFAQs', {
                productId: interestProduct.id,
            });
        }
    }, [interestProduct?.id]);
    const handleDepositInformation = useCallback(() => {
        if (interestProduct?.summary) {
            if (Platform.OS === 'android') {
                navigation.navigateDeprecated('Pdf', { url: interestProduct.summary });
            }
            else {
                LinkHelpers.openLink(interestProduct.summary, true);
            }
        }
    }, [interestProduct?.summary]);
    const renderInfo = useMemo(() => (<CardView noMarginBottom>
        <View style={styles.headerInfo}>
          <View style={styles.flex1}>
            {activePotInterest ? (<Text Text-24>
                Earn{' '}
                <Text Numbers-24 Primary>
                  {activePotInterest}%
                </Text>{' '}
                AER
              </Text>) : (<Text Text-24>Earn Interest</Text>)}
            {Boolean(interestProduct?.interestPaymentFrequency) && (<>
                <Spacer h={4}/>
                <Text TextThin-16 Secondary>
                  paid {interestProduct?.interestPaymentFrequency?.toLowerCase()}
                </Text>
              </>)}
          </View>
          <Spacer w={8}/>
          {interestProduct?.fscsBrochure ? (<Image source={FSCSImg} testID="FSCSImg" resizeMode="contain" tintColor={colors.elements.secondary} style={styles.fscsImg}/>) : (<Squircle color={colors.background.dark}>
              <IconPot colorKey="brand"/>
            </Squircle>)}
        </View>
        {!!interestProduct?.productScreenNotice && (<View style={styles.subTextContainer}>
            <Text TextThin-14 Secondary>
              {interestProduct?.productScreenNotice}
            </Text>
          </View>)}
        <View style={styles.line}/>
        {!!(interestProduct && interestProduct.infoList?.length) && (<View style={styles.listInfoContainer}>
            {interestProduct.infoList.map((info) => (<ListInfoItem title={info.text} url={info.url} key={info.text}/>))}
          </View>)}
      </CardView>), [
        activePotInterest,
        colors.background.dark,
        colors.elements.secondary,
        interestProduct,
        styles.flex1,
        styles.fscsImg,
        styles.headerInfo,
        styles.line,
        styles.listInfoContainer,
        styles.subTextContainer,
    ]);
    const handleTrusteeTnc = useCallback(() => {
        if (interestProduct?.termsAndConditions) {
            if (Platform.OS === 'android') {
                navigation.navigateDeprecated('Pdf', { url: interestProduct.termsAndConditions });
            }
            else {
                LinkHelpers.openLink(interestProduct.termsAndConditions, true);
            }
        }
    }, [interestProduct?.termsAndConditions]);
    const renderConsent = useMemo(() => createPotParams ? (<View style={styles.consentCardWrapper}>
          <TouchableBounce onPress={handleToggle}>
            <CardView noMargin style={styles.consentCard} withPadding>
              <RadioButton isChecked={value} type={'multiple'} testID="ReviewAndConfirmScreenSwitch"/>
              <Spacer w={16}/>
              <View style={styles.flex}>
                <Text TextThin-14>
                  I have reviewed{' '}
                  <Text TextThin-14 Primary suppressHighlighting onPress={handleFAQs}>
                    key information
                  </Text>
                  ,{' '}
                  <Text TextThin-14 Primary suppressHighlighting onPress={handleDepositInformation}>
                    deposit information
                  </Text>{' '}
                  and{' '}
                  <Text TextThin-14 Primary suppressHighlighting onPress={handleTrusteeTnc}>
                    trustee terms & conditions
                  </Text>{' '}
                  and I give my consent to proceed with the savings product
                </Text>
              </View>
            </CardView>
          </TouchableBounce>
        </View>) : null, [
        createPotParams,
        handleDepositInformation,
        handleFAQs,
        handleToggle,
        handleTrusteeTnc,
        styles.consentCard,
        styles.consentCardWrapper,
        styles.flex,
        value,
    ]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
      <StatusBar animated barStyle="dark-content"/>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} title={route.params?.createPotParams ? 'Review and confirm' : 'Rate information'}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        {renderInfo}
        {renderConsent}
      </Animated.ScrollView>
      {!!createPotParams && (<Button brand floating title="Continue" disabled={!value} onPress={handleContinue} isFetching={creatingPotRequesting} isInTab={!!mountedOnAnotherScreen}/>)}
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    flex1: {
        flex: 1,
    },
    switchSettingRight: {
        marginLeft: rem(12),
    },
    headerInfo: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    line: {
        height: 1,
        flex: 1,
        backgroundColor: colors.background.dark,
    },
    listInfoContainer: {
        flex: 1,
        padding: rem(16),
    },
    learnMoreContainer: {
        marginLeft: rem(36),
    },
    subTextContainer: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    consentCardWrapper: {
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
    consentCard: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    fscsImg: {
        width: rem(42),
        height: rem(30),
    },
}));
export default ReviewAndConfirmScreen;
